'use client'

import { useState, useEffect } from 'react'
import { captureEvent, EVENTS } from '@/lib/posthog/events'
import { createClient } from '@/lib/supabase/client'
import { getSiteURL } from '@/utils/siteUrl'
import Link from 'next/link'
import { CaptchaWidget } from '@/components/client/CaptchaWidget'
import { useCaptcha } from '@/hooks/useCaptcha'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const captcha = useCaptcha()
  const supabase = createClient()

  // Countdown timer for cooldown
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => {
        setCooldown(cooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [cooldown])

  const handleResendEmail = async () => {
    if (cooldown > 0) return

    setLoading(true)
    setResendMessage(null)

    try {
      const captchaToken = await captcha.getToken()
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${getSiteURL()}/reset-password`,
        captchaToken,
      })

      if (error) {
        const isCaptchaError =
          error.code === 'captcha_failed' ||
          (error.code === 'unexpected_failure' &&
            error.message?.toLowerCase().includes('captcha'))
        if (isCaptchaError) {
          console.warn('[captcha] verification failed', {
            code: error.code,
            message: error.message?.slice(0, 120),
          })
          setResendMessage('Bot check failed — please click Resend again.')
        } else if (error.message.toLowerCase().includes('rate limit')) {
          setResendMessage('Rate limit exceeded. Please wait 5-10 minutes before requesting another email.')
        } else {
          setResendMessage(error.message)
        }
      } else {
        setResendMessage('Email sent! Check your inbox.')
        setCooldown(60) // 60 second cooldown
      }
    } catch (_err) {
      setResendMessage('Failed to resend email. Please try again.')
    } finally {
      // Remount the widget so a retry gets a fresh single-use token (the prior
      // one was consumed by Supabase).
      captcha.refresh()
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (cooldown > 0) return

    setError(null)
    setLoading(true)
    captureEvent(EVENTS.PASSWORD_RESET_REQUESTED, {})

    try {
      const captchaToken = await captcha.getToken()
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${getSiteURL()}/reset-password`,
        captchaToken,
      })

      if (error) {
        const isCaptchaError =
          error.code === 'captcha_failed' ||
          (error.code === 'unexpected_failure' &&
            error.message?.toLowerCase().includes('captcha'))
        const isRateLimit = error.message.toLowerCase().includes('rate limit')
        captureEvent(EVENTS.PASSWORD_RESET_REQUEST_FAILED, {
          reason: isCaptchaError ? 'captcha_failed' : isRateLimit ? 'rate_limit' : (error.code ?? 'unknown'),
        })
        if (isCaptchaError) {
          console.warn('[captcha] verification failed', {
            code: error.code,
            message: error.message?.slice(0, 120),
          })
          setError('Bot check failed — please click Send reset link again.')
        } else if (isRateLimit) {
          setError('Too many password reset attempts. Please wait 5-10 minutes and try again.')
        } else {
          setError(error.message)
        }
      } else {
        captureEvent(EVENTS.PASSWORD_RESET_EMAIL_SENT, {})
        setSuccess(true)
        setCooldown(60) // 60 second cooldown
      }
    } catch (_err) {
      captureEvent(EVENTS.PASSWORD_RESET_REQUEST_FAILED, { reason: 'exception' })
      setError('An unexpected error occurred')
    } finally {
      // Remount the widget so a retry gets a fresh single-use token (the prior
      // one was consumed by Supabase).
      captcha.refresh()
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-bg dark:bg-bg-primary flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-bg-secondary rounded-lg shadow-lg p-8 border border-border dark:border-border-secondary">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-fg dark:text-text-primary mb-2">
                Check your email
              </h2>
              <p className="text-muted-foreground dark:text-text-secondary">
                We sent a password reset link to
              </p>
              <p className="text-fg dark:text-text-primary font-medium mt-1">
                {email}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-muted/50 dark:bg-bg-tertiary rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-fg dark:text-text-primary mb-2">
                Next steps:
              </h3>
              <ol className="space-y-2 text-sm text-muted-foreground dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-fg dark:text-text-primary">1.</span>
                  <span>Open the email from Sharpr</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-fg dark:text-text-primary">2.</span>
                  <span>Click the "Reset password" link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-fg dark:text-text-primary">3.</span>
                  <span>Create a new password</span>
                </li>
              </ol>
            </div>

            {/* Helpful Tips */}
            <div className="mb-6">
              <p className="text-sm text-muted-foreground dark:text-text-tertiary mb-3">
                <strong>Didn't receive the email?</strong>
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Check your spam or junk folder</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Make sure you entered the correct email address</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Wait a few minutes - it may take some time to arrive</span>
                </li>
              </ul>
            </div>

            {/* Resend Email */}
            <div className="mb-6">
              <div className="flex justify-center mb-3">
                <CaptchaWidget
                  key={captcha.renderKey}
                  ref={captcha.ref}
                  onSuccess={captcha.setToken}
                  onExpire={captcha.refresh}
                  onError={captcha.refresh}
                />
              </div>
              <button
                onClick={handleResendEmail}
                disabled={loading || cooldown > 0}
                className="w-full px-4 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? 'Sending...'
                  : cooldown > 0
                  ? `Resend email (wait ${cooldown}s)`
                  : 'Resend reset email'}
              </button>
              {resendMessage && (
                <p
                  role="alert"
                  aria-live="polite"
                  className={`mt-2 text-sm text-center ${
                    resendMessage.includes('sent')
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {resendMessage}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Link
                href="/login"
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md font-medium transition-all duration-200 shadow-sm text-center"
              >
                Back to Sign In
              </Link>
              <button
                onClick={() => {
                  setSuccess(false)
                  setEmail('')
                }}
                className="w-full px-4 py-3 bg-white dark:bg-bg-tertiary text-fg dark:text-text-primary border border-border dark:border-border-secondary rounded-md hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200 font-medium text-center"
              >
                Try Another Email
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg dark:bg-bg-primary flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-fg dark:text-text-primary mb-2">
            Reset your password
          </h1>
          <p className="text-muted-foreground dark:text-text-secondary">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Reset Card */}
        <div className="bg-white dark:bg-bg-secondary rounded-lg shadow-lg p-8 border border-border dark:border-border-secondary">
          {/* Error Message */}
          {error && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
            >
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Email Form */}
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-fg dark:text-text-primary mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="w-full px-3 py-2 bg-white dark:bg-bg-tertiary border border-border dark:border-border-secondary rounded-md text-fg dark:text-text-primary placeholder:text-muted-foreground dark:placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
                placeholder="you@example.com"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="flex justify-center">
              <CaptchaWidget
                key={captcha.renderKey}
                ref={captcha.ref}
                onSuccess={captcha.setToken}
                onExpire={captcha.refresh}
                onError={captcha.refresh}
              />
            </div>

            <button
              type="submit"
              disabled={loading || cooldown > 0}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading
                ? 'Sending...'
                : cooldown > 0
                ? `Wait ${cooldown}s before trying again`
                : 'Send reset link'}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              href="/login"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
